import { useLayoutEffect, useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import type { Rig } from '../../animation/rig';
import { palette } from '../../theme';

// ---------------------------------------------------------------------------
// Debug visual for the squirrel rig.
//
// Renders a sphere at each joint and a cylinder along each parent→child
// edge. Updated every frame from world positions, so any IK-driven
// changes to bone positions just work.
//
// Styling: warm origami-ish palette (acorn-orange joints, beige
// connectors). The character isn't a squirrel yet — it's a stick figure
// with bulbs. The silhouette test will tell us if motion alone reads as
// alive on this form.
// ---------------------------------------------------------------------------

const JOINT_RADIUS = 0.02;
const CONNECTOR_RADIUS = 0.012;

// Shared geometries — module-level singletons. The rig has 26 joint
// spheres + 25 connector cylinders, each rendered as a separate mesh,
// but they all use the same shape. One geometry per type means one GPU
// upload instead of 51, regardless of how many rigs the page is showing.
const JOINT_GEOMETRY = new THREE.SphereGeometry(JOINT_RADIUS, 12, 8);
const CONNECTOR_GEOMETRY = new THREE.CylinderGeometry(
  CONNECTOR_RADIUS,
  CONNECTOR_RADIUS,
  1,
  10,
);

interface JointPair {
  parent: THREE.Object3D;
  child: THREE.Bone;
}

export function RigVisual({ rig }: { rig: Rig }) {
  // Build the parent→child pair list once per rig.
  const pairs = useMemo<JointPair[]>(() => {
    const out: JointPair[] = [];
    for (const bone of rig.allBones) {
      if (bone.parent && (bone.parent as THREE.Bone).isBone) {
        out.push({ parent: bone.parent as THREE.Bone, child: bone });
      }
    }
    return out;
  }, [rig]);

  // The bones live in rig.root (an orphan THREE.Group, not attached to
  // the scene) and have no geometry of their own. Visuals are the
  // JointSpheres / ConnectorCylinders groups, which read bone world
  // positions each frame and place mirror meshes accordingly.
  return (
    <>
      <JointSpheres bones={rig.allBones} />
      <ConnectorCylinders pairs={pairs} />
    </>
  );
}

function JointSpheres({ bones }: { bones: THREE.Bone[] }) {
  const groupRef = useRef<THREE.Group>(null);
  const tmp = useMemo(() => new THREE.Vector3(), []);

  function sync() {
    const group = groupRef.current;
    if (!group) return;
    if (bones.length > 0) {
      let root: THREE.Object3D = bones[0];
      while (root.parent) root = root.parent;
      root.updateMatrixWorld(true);
    }
    for (let i = 0; i < bones.length; i++) {
      const child = group.children[i];
      if (!child) continue;
      bones[i].getWorldPosition(tmp);
      child.position.copy(tmp);
    }
  }

  useLayoutEffect(() => {
    sync();
  });

  useFrame(sync);

  return (
    <group ref={groupRef}>
      {bones.map(bone => (
        <mesh
          key={bone.uuid}
          castShadow
          frustumCulled={false}
          geometry={JOINT_GEOMETRY}
        >
          <meshStandardMaterial color={palette.acornOrange} roughness={0.5} />
        </mesh>
      ))}
    </group>
  );
}

function ConnectorCylinders({ pairs }: { pairs: JointPair[] }) {
  const groupRef = useRef<THREE.Group>(null);
  const a = useMemo(() => new THREE.Vector3(), []);
  const b = useMemo(() => new THREE.Vector3(), []);
  const dir = useMemo(() => new THREE.Vector3(), []);
  const up = useMemo(() => new THREE.Vector3(0, 1, 0), []);
  const q = useMemo(() => new THREE.Quaternion(), []);

  function sync() {
    const group = groupRef.current;
    if (!group) return;
    if (pairs.length > 0) {
      let root: THREE.Object3D = pairs[0].child;
      while (root.parent) root = root.parent;
      root.updateMatrixWorld(true);
    }
    for (let i = 0; i < pairs.length; i++) {
      const child = group.children[i];
      if (!child) continue;
      pairs[i].parent.getWorldPosition(a);
      pairs[i].child.getWorldPosition(b);
      dir.subVectors(b, a);
      const len = dir.length();
      if (len < 1e-6) {
        child.scale.set(1, 0.0001, 1);
        continue;
      }
      dir.divideScalar(len);
      child.position.copy(a).addScaledVector(dir, len * 0.5);
      q.setFromUnitVectors(up, dir);
      child.quaternion.copy(q);
      child.scale.set(1, len, 1);
    }
  }

  // Sync once on mount (before first paint) so the cylinders aren't
  // visible at their default unit length on the first rendered frame.
  useLayoutEffect(() => {
    sync();
  });

  useFrame(sync);

  return (
    <group ref={groupRef}>
      {pairs.map((_, i) => (
        <mesh
          key={i}
          castShadow
          frustumCulled={false}
          geometry={CONNECTOR_GEOMETRY}
        >
          <meshStandardMaterial color={palette.warmBeige} roughness={0.6} />
        </mesh>
      ))}
    </group>
  );
}
